import { useState } from 'react';
import { Box, Modal as ModalContainer, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { SMBold, Title, SubTitle } from '@/typography/Typography';
import { InputText } from '@/components/shared/InputText';
import { Dropdown } from '@/components/shared/Dropdown';
import { IOSSwitch } from '@/components/shared/Switch';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { UploadFile } from '@/pages/company/UploadFile';
import { StyledDatePicker } from '@/components/shared/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useUsersMutations } from '@/services/usersMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { DATE_FORMAT } from '@/lib/constants';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';

interface UploadLegalDocumentDialogProps {
    open: boolean;
    onClose: () => void;
}

export const UploadLegalDocumentDialog = ({
    open,
    onClose
}: UploadLegalDocumentDialogProps) => {
    const { t } = useTranslation();
    const { uploadLegalDocument } = useUsersMutations();

    const [consentType, setConsentType] = useState<string>('Data');
    const [documentName, setDocumentName] = useState<string>('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [_fileUrl, setFileUrl] = useState<string>('');
    const [signingStatus, setSigningStatus] = useState<
        'requireSigning' | 'alreadySigned'
    >('alreadySigned');
    const [signedDate, setSignedDate] = useState<Dayjs | null>(null);
    const [signerName, setSignerName] = useState<string | null>(null);

    const documentTypeChoices = [
        {
            label: t('data_processing_agreement'),
            value: 'Data'
        },
        {
            label: t('license_agreement'),
            value: 'Commercial'
        }
    ];

    const handleFileUpload = (file: File) => {
        setUploadedFile(file);
        setDocumentName(file.name);
    };

    const handleClose = () => {
        setConsentType('Data');
        setDocumentName('');
        setUploadedFile(null);
        setFileUrl('');
        setSigningStatus('alreadySigned');
        setSignedDate(null);
        setSignerName(null);
        onClose();
    };

    useHandleResponse({
        error: uploadLegalDocument.error,
        isSuccess: uploadLegalDocument.isSuccess,
        successAlertMessage: t('document_uploaded_successfully'),
        isSuccessCallBack: handleClose
    });

    const { data: companyData } = useCurrentCompanyDetails();

    const handleUpload = async () => {
        if (!uploadedFile || !consentType) {
            return;
        }

        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('consentType', consentType);
        formData.append('name', documentName);
        formData.append('comment', '');
        if (signingStatus === 'alreadySigned') {
            if (signedDate) {
                formData.append('signedAt', signedDate.format('YYYY-MM-DD'));
            }
            if (signerName) {
                formData.append('signedBy', signerName);
            }
        }
        formData.append('companyId', companyData?.data?.companyId!!);
        formData.append('latestAcceptanceDate', dayjs().format('YYYY-MM-DD'));

        uploadLegalDocument.mutate(formData);
    };

    const isFormValid =
        uploadedFile &&
        consentType &&
        (signingStatus === 'requireSigning' ||
            (signingStatus === 'alreadySigned' && signedDate && signerName));

    return (
        <ModalContainer open={open} onClose={handleClose}>
            <ConfirmationBox style={{ width: '450px' }}>
                <ConnectHeader
                    title={t('upload_new_legal_document')}
                    description=""
                />

                <Box alignItems={'flex-start'} mt={2} mb={1}>
                    <Title mb={0.5}>{t('replace_what_document')}</Title>
                    <Dropdown
                        label={''}
                        choices={documentTypeChoices}
                        value={consentType}
                        onChange={(value) => setConsentType(value || 'Data')}
                        allowClear={false}
                    />

                    <Box mt={2}>
                        <UploadFile
                            handleFileUpload={handleFileUpload}
                            onUploadSuccess={(url) => setFileUrl(url)}
                            description={t('pdf_max_size_description')}
                            accept={{
                                'application/pdf': ['.pdf']
                            }}
                        />
                    </Box>

                    {uploadedFile && (
                        <Typography fontSize={12} mt={1} color="text.secondary">
                            {uploadedFile.name}
                        </Typography>
                    )}

                    <Box mt={2}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <IOSSwitch
                                checked={signingStatus === 'alreadySigned'}
                                onChange={(e) => {
                                    const value = e.target.checked
                                        ? 'alreadySigned'
                                        : 'requireSigning';
                                    setSigningStatus(value);
                                    if (value === 'requireSigning') {
                                        setSignedDate(null);
                                        setSignerName(null);
                                    }
                                }}
                            />
                            <Title mb={0}>
                                This document is already signed
                            </Title>
                        </Box>
                    </Box>

                    {signingStatus === 'alreadySigned' && (
                        <>
                            <Box mt={2}>
                                <Title mb={0.5}>
                                    {t('when_was_document_signed')}
                                </Title>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <StyledDatePicker
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                id: 'outlined-basic',
                                                variant: 'outlined'
                                            }
                                        }}
                                        format={DATE_FORMAT.BASIC_DATA_YEAR}
                                        value={signedDate}
                                        onChange={(newValue: Dayjs | null) => {
                                            if (newValue) {
                                                setSignedDate(dayjs(newValue));
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <SubTitle mt={0.5}>
                                    {t('use_datepicker_to_select_date')}
                                </SubTitle>
                            </Box>

                            <Box mt={2}>
                                <InputText
                                    label={t('who_signed_document')}
                                    placeholder={t('write_full_name')}
                                    value={signerName}
                                    onChange={(e) =>
                                        setSignerName(e.target.value)
                                    }
                                />
                            </Box>
                        </>
                    )}
                </Box>

                <Box
                    width={'100%'}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 4,
                        mb: 2
                    }}
                    gap={1.5}
                >
                    <OutlinedSecondaryButtonLG
                        sx={{ width: '50%' }}
                        onClick={handleClose}
                    >
                        <SMBold textTransform={'none'}>{t('cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>

                    <PrimaryButtonLG
                        sx={{ width: '50%' }}
                        onClick={handleUpload}
                        disabled={!isFormValid || uploadLegalDocument.isPending}
                    >
                        <SMBold textTransform={'none'}>{t('upload')}</SMBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
